import React from 'react';
import { FaFilePdf, FaIdCard, FaEdit } from 'react-icons/fa';
import { Card, Alert, Button, Row, Col } from 'react-bootstrap';
import { UserModel } from '../../../models/UserModel';

const API_URL = process.env.REACT_APP_API_BASE;

interface ProfessionalDocumentsTabProps {
  localUser: UserModel;
  primaryColor: string;
  onShowRibModal: () => void;
  getDisplayedRib: () => string;
  getDisplayedBic: () => string;
  formatRIB: (rib?: string | null) => string;
}

const ProfessionalDocumentsTab: React.FC<ProfessionalDocumentsTabProps> = ({
  localUser,
  primaryColor,
  onShowRibModal,
  getDisplayedRib,
  getDisplayedBic,
  formatRIB,
}) => {
  const docUrl = (path?: string) => {
    if (!path) return '';
    if (path.startsWith('http') || path.startsWith('blob:')) return path;
    return `${API_URL}/${path}`;
  };

  if (localUser.user_type !== 'professionnel') {
    return (
      <div className="text-center text-muted py-4">
        <p>Cette section est réservée aux comptes professionnels</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <Alert variant="info" className="mb-3">
        <strong>Statut de vérification :</strong>{' '}
        {localUser.documents_verified ? (
          <span className="badge bg-success">✓ Vérifié</span>
        ) : (
          <span className="badge bg-warning">En attente de vérification</span>
        )}
        {localUser.documents_verification_date && (
          <div className="mt-2">
            <small>
              Vérifié le : {new Date(localUser.documents_verification_date).toLocaleDateString('fr-FR')}
            </small>
          </div>
        )}
      </Alert>

      {/* Kbis */}
      <Card className="mb-3">
        <Card.Header style={{ backgroundColor: primaryColor, color: 'white' }}>
          <FaFilePdf /> Extrait Kbis
        </Card.Header>
        <Card.Body>
          {localUser.kbis_document ? (
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <p className="mb-2">Document Kbis au format PDF</p>
                <small className="text-muted">
                  Fichier : {docUrl((localUser as any).kbis_document).split('/').pop()}
                </small>
              </div>
              <Button
                className="btn-primary-custom"
                onClick={() => window.open(docUrl((localUser as any).kbis_document), '_blank')}
              >
                <FaFilePdf style={{ marginRight: "10px" }} /> Voir le document
              </Button>
            </div>
          ) : (
            <p className="text-muted">Aucun document Kbis disponible</p>
          )}
        </Card.Body>
      </Card>

      {/* Pièce d'identité */}
      <Card className="mb-3">
        <Card.Header style={{ backgroundColor: primaryColor, color: 'white' }}>
          <FaIdCard /> Pièce d'identité
        </Card.Header>
        <Card.Body>
          <p className="mb-3">
            <strong>Type :</strong>{' '}
            <span className="badge bg-secondary">
              {localUser.identity_document_type === 'cin' ? 'Carte d\'Identité Nationale' : 'Passeport'}
            </span>
          </p>

          <Row>
            <Col md={6}>
              <h6>Recto {localUser.identity_document_type === 'passeport' && '(Page principale)'}</h6>
              {localUser.identity_document_recto ? (
                <div>
                  <img
                    src={docUrl((localUser as any).identity_document_recto)}
                    alt="Document recto"
                    style={{
                      width: '100%',
                      maxHeight: '300px',
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      marginBottom: '10px',
                      cursor: 'pointer'
                    }}
                    onClick={() => window.open(docUrl((localUser as any).identity_document_recto), '_blank')}
                  />
                  <Button
                    style={{ backgroundColor: '#eb8635', border: 'none', color: 'white' }}
                    size="sm"
                    className="w-100"
                    onClick={() => window.open(docUrl((localUser as any).identity_document_recto), '_blank')}
                  >
                    Voir en grand
                  </Button>
                </div>
              ) : (
                <p className="text-muted">Non disponible</p>
              )}
            </Col>

            {localUser.identity_document_type === 'cin' && (
              <Col md={6}>
                <h6>Verso</h6>
                {localUser.identity_document_verso ? (
                  <div>
                    <img
                      src={docUrl((localUser as any).identity_document_verso)}
                      alt="Document verso"
                      style={{
                        width: '100%',
                        maxHeight: '300px',
                        objectFit: 'contain',
                        border: '1px solid #ddd',
                        borderRadius: '5px',
                        marginBottom: '10px',
                        cursor: 'pointer'
                      }}
                      onClick={() => window.open(docUrl((localUser as any).identity_document_verso), '_blank')}
                    />
                    <Button
                      size="sm"
                      className="w-100"
                      onClick={() => window.open(docUrl((localUser as any).identity_document_verso), '_blank')}
                      style={{ backgroundColor: '#eb8635', border: 'none', color: 'white' }}
                    >
                      Voir en grand
                    </Button>
                  </div>
                ) : (
                  <p className="text-muted">Non disponible</p>
                )}
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {/* RIB */}
      <Card className="mb-3">
        <Card.Header style={{ backgroundColor: primaryColor, color: 'white' }}>
          <i className="me-2">🏦</i> Relevé d'Identité Bancaire (RIB)
        </Card.Header>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start flex-column flex-md-row">
            <div style={{ flex: 1 }}>
              <h6 className="mb-2">Document RIB téléchargé</h6>
              {(localUser as any).rib_document ? (
                <>
                  <small className="text-muted d-block mb-2">
                    Fichier: {docUrl((localUser as any).rib_document).split('/').pop()}
                  </small>
                  <div className="d-flex gap-2">
                    <Button
                      style={{ backgroundColor: primaryColor, border: 'none' }}
                      onClick={() => window.open(docUrl((localUser as any).rib_document), '_blank')}
                    >
                      <FaFilePdf className="me-2" /> Voir le RIB
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-muted mb-2">Aucun document RIB téléchargé</p>
                  <Button
                    className="btn-primary-custom"
                    onClick={onShowRibModal}
                  >
                    <FaEdit className="me-2" /> Ajouter / Modifier RIB
                  </Button>
                </>
              )}
            </div>

            <div style={{ minWidth: 260 }}>
              <h6>Coordonnées bancaires enregistrées</h6>
              <div style={{ padding: 12, border: '1px solid #e9ecef', borderRadius: 6 }}>
                <div>
                  <strong>RIB:</strong>
                  <div style={{ wordBreak: 'break-all' }}>
                    {getDisplayedRib() ? (
                      <span>{formatRIB(getDisplayedRib()) || getDisplayedRib()}</span>
                    ) : (
                      <span className="text-muted">Non fourni</span>
                    )}
                  </div>
                </div>
                <div className="mt-2">
                  <strong>BIC / SWIFT:</strong>
                  <div>
                    {getDisplayedBic() ? (
                      <span>{getDisplayedBic()}</span>
                    ) : (
                      <span className="text-muted">Non fourni</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Guide de vérification */}
      <div className="verification-guide mt-3">
        <div className="guide-header">
          <i className="bi bi-info-circle"></i>
          <h4>Guide de vérification</h4>
        </div>
        <ul className="guide-list">
          <li><i className="bi bi-check-circle"></i> Kbis datant de moins de 3 mois</li>
          <li><i className="bi bi-check-circle"></i> Pièce d'identité en cours de validité</li>
          <li><i className="bi bi-check-circle"></i> Document RIB lisible et complet</li>
          <li><i className="bi bi-check-circle"></i> Correspondance RIB document vs RIB saisi</li>
          <li><i className="bi bi-check-circle"></i> Correspondance des informations (SIREN, nom, BIC)</li>
          <li><i className="bi bi-check-circle"></i> Documents lisibles et complets</li>
        </ul>
      </div>
    </div>
  );
};

export default ProfessionalDocumentsTab;